import React, { InputHTMLAttributes, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { nanoid } from 'nanoid'
import { ErrorDetail } from '../ErrorDetail/ErrorDetail'

const StyledFileInput = styled.input`
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
`

const Field = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  row-gap: 0.25em;
`

const InputContainer = styled.div<Omit<Props, 'label'>>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25em;

  > label {
    cursor: pointer;
    padding: 12px 24px;
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.font.white};
    background-color: ${({ theme }) => theme.colors.button.primary};
    border: none;
    border: 1px solid ${({ theme }) => theme.colors.button.primary};

    &:hover,
    &:focus {
      color: ${({ theme }) => theme.colors.font.inverse};
    }

    &:active {
      border: 1px solid
        ${({ theme }) => theme.colors.system.interactive.selected};
    }

    ${(props) =>
      props.disabled &&
      css`
        border: 1px solid
          ${({ theme }) => theme.colors.system.interactive.disabled};

        &:hover,
        &:focus,
        &:active {
          border: 1px solid
            ${({ theme }) => theme.colors.system.interactive.disabled};
        }
      `}
  }
`

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  hideLabel?: boolean
  error?: React.ReactNode
}

// Reference for this component for why it uses the label https://tympanus.net/codrops/2015/09/15/styling-customizing-file-inputs-smart-way/
// Also firefox doesn't support :before and :after in the file input and this allows for more customability
export const FileInput: React.FC<Props> = React.forwardRef<
  HTMLInputElement,
  Props
>(
  (
    {
      className,
      error,
      hideLabel,
      label,
      name,
      required,
      value,
      onBlur,
      onChange,
      id,
      ...rest
    },
    ref,
  ) => {
    // This ensures a label is always matched to an input
    const inputId = useMemo(() => {
      return id ? id : `FileInput__input-id-${nanoid()}`
    }, [id])

    return (
      <Field>
        <InputContainer className={className}>
          <label htmlFor={inputId} hidden={hideLabel || rest.hidden}>
            {label}
          </label>
          <StyledFileInput
            id={inputId}
            aria-label={!hideLabel ? label : undefined}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            ref={ref}
            // TODO: Figure out HEIC
            accept="image/jpeg, image/png"
            {...rest}
            type="file" // Don't want this to be overwritten by spread
          />
        </InputContainer>
        {error ? <ErrorDetail error={error} /> : null}
      </Field>
    )
  },
)

FileInput.displayName = 'FileInput'
